/**
 * HrJobTitlesPage 컴포넌트
 *
 * 직책(Job Title) 마스터 관리 페이지입니다.
 *
 * **기능:**
 * - 직책 목록 조회 및 표시
 * - 직책 생성/수정/삭제 (CRUD)
 * - 검색 및 필터링
 * - order 순서 정렬
 *
 * **권한:**
 * - hr.manage 권한 필요 (HR, Super Admin만 접근 가능)
 */

import React, { useState } from 'react';
import { Search, Plus, Edit2, Trash2, Loader, AlertCircle, Award } from 'lucide-react';
import { useJobTitles } from '../../shared/hooks/useJobTitles';
import { useJobTitleMutations } from '../../shared/hooks/useJobTitleMutations';
import { JobTitleFormModal } from './components/JobTitleFormModal';

/**
 * 직책 관리 페이지 컴포넌트
 */
export function HrJobTitlesPage() {
  // 검색/필터 상태
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // 모달 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingJobTitleId, setEditingJobTitleId] = useState<string | undefined>(undefined);

  // 데이터 조회
  const { jobTitles, isLoading, error, refetch } = useJobTitles({
    searchText,
    status: statusFilter,
  });

  // Mutations
  const { deleteJobTitle } = useJobTitleMutations();

  /**
   * 직책 추가 버튼 클릭
   */
  const handleAdd = () => {
    setEditingJobTitleId(undefined); // 생성 모드
    setIsModalOpen(true);
  };

  /**
   * 직책 수정 버튼 클릭
   */
  const handleEdit = (jobTitleId: string) => {
    setEditingJobTitleId(jobTitleId); // 수정 모드
    setIsModalOpen(true);
  };

  /**
   * 직책 삭제 버튼 클릭
   */
  const handleDelete = async (jobTitleId: string, jobTitleName: string) => {
    const confirmed = window.confirm(
      `"${jobTitleName}" 직책을 삭제하시겠습니까?\n\n` +
        `소프트 삭제되며, 사용 여부가 "미사용"으로 변경됩니다.\n` +
        `해당 직책을 가진 직원이 있어도 삭제 가능합니다.`
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteJobTitle(jobTitleId);
      alert('직책이 삭제되었습니다.');
      refetch(); // 목록 새로고침
    } catch (err: any) {
      console.error('직책 삭제 실패:', err);
      alert(err.message || '직책 삭제에 실패했습니다.');
    }
  };

  /**
   * 모달 저장 성공 시
   */
  const handleModalSuccess = () => {
    refetch(); // 목록 새로고침
    setIsModalOpen(false);
  };

  /**
   * 필터 초기화
   */
  const handleResetFilters = () => {
    setSearchText('');
    setStatusFilter('all');
  };

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>직책 관리</h1>
          <p style={styles.subtitle}>
            직책(Job Title) 마스터 데이터를 관리합니다. (팀원, 담당, 팀장, 실장, 본부장, 대표)
          </p>
        </div>
        <button
          style={styles.addButton}
          onClick={handleAdd}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 150, 136, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          }}
        >
          <Plus size={20} />
          <span>직책 추가</span>
        </button>
      </div>

      {/* 검색/필터 영역 */}
      <div style={styles.filterSection}>
        {/* 검색 */}
        <div style={styles.searchBox}>
          <Search size={20} style={{ color: 'var(--color-gray-400)' }} />
          <input
            type="text"
            placeholder="직책명, 코드 검색..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        {/* 사용 여부 필터 */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={styles.select}
        >
          <option value="all">전체</option>
          <option value="active">사용</option>
          <option value="inactive">미사용</option>
        </select>

        {/* 필터 초기화 */}
        {(searchText || statusFilter !== 'all') && (
          <button style={styles.resetButton} onClick={handleResetFilters}>
            필터 초기화
          </button>
        )}
      </div>

      {/* 로딩 상태 */}
      {isLoading && (
        <div style={styles.loadingBox}>
          <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: '12px', color: 'var(--color-gray-600)' }}>
            직책 목록을 불러오는 중...
          </p>
        </div>
      )}

      {/* 에러 상태 */}
      {error && !isLoading && (
        <div style={styles.errorBox}>
          <AlertCircle size={32} color="var(--color-error)" />
          <p style={{ marginTop: '12px', color: 'var(--color-error)', fontWeight: '500' }}>
            직책 목록 조회에 실패했습니다.
          </p>
          <p style={{ marginTop: '4px', color: 'var(--color-gray-600)', fontSize: '14px' }}>
            {error.message}
          </p>
          <button style={styles.retryButton} onClick={() => refetch()}>
            다시 시도
          </button>
        </div>
      )}

      {/* 직책 목록 테이블 */}
      {!isLoading && !error && (
        <>
          {jobTitles.length === 0 ? (
            <div style={styles.emptyBox}>
              <Award size={48} style={{ color: 'var(--color-gray-400)' }} />
              <p style={{ marginTop: '16px', color: 'var(--color-gray-600)' }}>
                {searchText || statusFilter !== 'all'
                  ? '검색 결과가 없습니다.'
                  : '등록된 직책이 없습니다.'}
              </p>
              {!searchText && statusFilter === 'all' && (
                <button style={styles.addButtonInEmpty} onClick={handleAdd}>
                  <Plus size={18} />
                  <span>첫 직책 추가하기</span>
                </button>
              )}
            </div>
          ) : (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: '60px' }}>순서</th>
                    <th style={styles.th}>직책명</th>
                    <th style={styles.th}>코드</th>
                    <th style={styles.th}>단일 직책</th>
                    <th style={styles.th}>사용 여부</th>
                    <th style={styles.th}>설명</th>
                    <th style={{ ...styles.th, width: '120px' }}>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {jobTitles.map((jobTitle) => (
                    <tr key={jobTitle.id} style={styles.tr}>
                      <td style={styles.td}>
                        <strong>{jobTitle.order}</strong>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Award size={18} color="var(--color-primary)" />
                          <strong>{jobTitle.name}</strong>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <code style={styles.code}>{jobTitle.code}</code>
                      </td>
                      <td style={styles.td}>
                        {jobTitle.isUnique ? (
                          <span style={styles.uniqueBadge}>단일</span>
                        ) : (
                          <span style={{ color: 'var(--color-gray-400)' }}>-</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            backgroundColor:
                              jobTitle.status === 'active' ? '#E8F5E9' : '#F5F5F5',
                            color: jobTitle.status === 'active' ? '#4CAF50' : '#9E9E9E',
                          }}
                        >
                          {jobTitle.status === 'active' ? '사용' : '미사용'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>
                          {jobTitle.description || '-'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actionButtons}>
                          <button
                            style={styles.editButton}
                            onClick={() => handleEdit(jobTitle.id)}
                            title="수정"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            style={styles.deleteButton}
                            onClick={() => handleDelete(jobTitle.id, jobTitle.name)}
                            title="삭제"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 결과 개수 */}
          {jobTitles.length > 0 && (
            <div style={styles.footer}>
              <p style={{ color: 'var(--color-gray-600)', fontSize: '14px' }}>
                총 <strong>{jobTitles.length}</strong>개의 직책
              </p>
            </div>
          )}
        </>
      )}

      {/* 직책 추가/수정 모달 */}
      <JobTitleFormModal
        isOpen={isModalOpen}
        jobTitleId={editingJobTitleId}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
}

/**
 * 스타일
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: 'var(--color-gray-800)',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--color-gray-600)',
    margin: 0,
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  filterSection: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
    flexWrap: 'wrap' as const,
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    flex: 1,
    minWidth: '300px',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    color: 'var(--color-gray-800)',
  },
  select: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none',
  },
  resetButton: {
    padding: '10px 20px',
    fontSize: '14px',
    color: 'var(--color-gray-700)',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
  },
  errorBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
  },
  retryButton: {
    marginTop: '16px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  emptyBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
  },
  addButtonInEmpty: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '16px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    padding: '16px',
    textAlign: 'left' as const,
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
    backgroundColor: 'var(--color-gray-50)',
    borderBottom: '2px solid var(--color-gray-200)',
    whiteSpace: 'nowrap' as const,
  },
  tr: {
    transition: 'background-color 0.2s',
    borderBottom: '1px solid var(--color-gray-200)',
  },
  td: {
    padding: '16px',
    fontSize: '14px',
    color: 'var(--color-gray-800)',
  },
  code: {
    padding: '4px 8px',
    fontSize: '12px',
    fontFamily: 'monospace',
    backgroundColor: 'var(--color-gray-100)',
    borderRadius: '4px',
    color: 'var(--color-gray-700)',
  },
  uniqueBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '500',
    backgroundColor: '#FFF3E0',
    color: '#F57C00',
    borderRadius: '12px',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '500',
    borderRadius: '12px',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  editButton: {
    padding: '6px',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '4px',
    cursor: 'pointer',
    color: 'var(--color-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  deleteButton: {
    padding: '6px',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '4px',
    cursor: 'pointer',
    color: 'var(--color-error)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  footer: {
    marginTop: '16px',
    textAlign: 'center' as const,
  },
};

// Loader 애니메이션을 위한 스타일 추가
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
document.head.appendChild(style);
